import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import api from '../../lib/api';

const RechargeTab = () => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'orange_money',
    customer_phone: ''
  });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await api.get('/payment/history?limit=10');
      setHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setMessage(null);

      const amount = parseInt(formData.amount);
      
      if (amount < 10000) {
        setMessage({ type: 'error', text: 'Montant minimum: 10,000 GNF' });
        return;
      }

      if (amount > 100000000) {
        setMessage({ type: 'error', text: 'Montant maximum: 100,000,000 GNF' });
        return;
      }

      // Récupérer l'entreprise_id depuis le contexte auth
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      if (!user || !user.entreprise_id) {
        setMessage({ type: 'error', text: 'Erreur: Entreprise non identifiée' });
        return;
      }

      const response = await api.post('/payment/initiate', {
        entreprise_id: user.entreprise_id,
        amount: amount,
        payment_method: formData.payment_method,
        customer_phone: formData.customer_phone || null,
        description: `Recharge solde ${new Date().toLocaleDateString('fr-FR')}`
      });

      if (response.data.success && response.data.payment_url) {
        // Rediriger vers PayCard
        window.location.href = response.data.payment_url;
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Erreur lors de l\'initiation du paiement' 
        });
      }
    } catch (error) {
      console.error('Erreur recharge:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erreur lors de la recharge' 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      'initiated': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Initié' },
      'processing': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      'success': { bg: 'bg-green-100', text: 'text-green-800', label: 'Réussi' },
      'failed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Échoué' },
      'expired': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Expiré' }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      'orange_money': '🟠',
      'mtn_momo': '🟡',
      'credit_card': '💳'
    };
    return icons[method] || '💰';
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">💰 Recharge du Solde</h2>
        <p className="text-gray-600 text-sm mt-1">
          Rechargez le solde de votre entreprise via Orange Money, MTN MoMo ou Carte bancaire
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire de recharge */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Nouvelle Recharge</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Montant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant à recharger (GNF) *
                </label>
                <input
                  type="number"
                  min="10000"
                  max="100000000"
                  step="1000"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="Ex: 5000000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-600 focus:border-transparent text-lg font-semibold"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum: 10,000 GNF | Maximum: 100,000,000 GNF
                </p>
              </div>

              {/* Méthode de paiement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Méthode de paiement *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-orange-500 transition-colors">
                    <input
                      type="radio"
                      name="payment_method"
                      value="orange_money"
                      checked={formData.payment_method === 'orange_money'}
                      onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                      className="mr-3"
                    />
                    <span className="text-2xl mr-2">🟠</span>
                    <span className="font-medium">Orange Money</span>
                  </label>

                  <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-yellow-500 transition-colors">
                    <input
                      type="radio"
                      name="payment_method"
                      value="mtn_momo"
                      checked={formData.payment_method === 'mtn_momo'}
                      onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                      className="mr-3"
                    />
                    <span className="text-2xl mr-2">🟡</span>
                    <span className="font-medium">MTN Mobile Money</span>
                  </label>

                  <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                    <input
                      type="radio"
                      name="payment_method"
                      value="credit_card"
                      checked={formData.payment_method === 'credit_card'}
                      onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                      className="mr-3"
                    />
                    <span className="text-2xl mr-2">💳</span>
                    <span className="font-medium">Carte Bancaire</span>
                  </label>
                </div>
              </div>

              {/* Téléphone (optionnel) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone (optionnel)
                </label>
                <input
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  placeholder="621234567"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-600 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Numéro pour recevoir la confirmation SMS
                </p>
              </div>

              {/* Message */}
              {message && (
                <div className={`p-3 rounded-lg ${
                  message.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
                }`}>
                  {message.text}
                </div>
              )}

              {/* Bouton */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold py-3"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Traitement en cours...
                  </span>
                ) : (
                  '💳 Procéder au paiement'
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                🔒 Paiement sécurisé via PayCard
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Informations */}
        <div className="space-y-4">
          <Card className="shadow-lg bg-gradient-to-br from-violet-50 to-purple-50">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">ℹ️ Comment ça marche ?</h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex">
                  <span className="font-bold mr-2">1️⃣</span>
                  <span>Choisissez le montant et la méthode de paiement</span>
                </div>
                <div className="flex">
                  <span className="font-bold mr-2">2️⃣</span>
                  <span>Cliquez sur "Procéder au paiement"</span>
                </div>
                <div className="flex">
                  <span className="font-bold mr-2">3️⃣</span>
                  <span>Vous serez redirigé vers la page de paiement sécurisée PayCard</span>
                </div>
                <div className="flex">
                  <span className="font-bold mr-2">4️⃣</span>
                  <span>Confirmez le paiement sur votre Mobile Money ou Carte</span>
                </div>
                <div className="flex">
                  <span className="font-bold mr-2">5️⃣</span>
                  <span>Votre solde est mis à jour automatiquement en quelques secondes</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">✅ Avantages</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Paiement instantané et sécurisé</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Plusieurs moyens de paiement disponibles</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Confirmation SMS automatique</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Historique complet des transactions</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Historique des recharges */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle>📜 Historique des Recharges</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 opacity-50">💳</div>
              <p className="text-gray-500 font-medium">Aucune recharge pour le moment</p>
              <p className="text-sm text-gray-400 mt-2">Vos transactions apparaîtront ici</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Référence</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Méthode</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 text-sm">Montant</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((transaction) => (
                    <tr key={transaction.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {new Date(transaction.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 text-sm font-mono text-gray-600">
                        {transaction.merchant_reference}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className="flex items-center">
                          <span className="mr-2">{getPaymentMethodIcon(transaction.payment_method)}</span>
                          <span className="text-gray-700">
                            {transaction.payment_method === 'orange_money' ? 'Orange Money' :
                             transaction.payment_method === 'mtn_momo' ? 'MTN MoMo' :
                             'Carte Bancaire'}
                          </span>
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-gray-900">
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(transaction.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RechargeTab;
